<template>
  <div class="quiz-play-page questionnaire-theme">
    <van-loading v-if="loading && !quiz.id" class="loading-center" />

    <div v-else-if="invalidQuery" class="state-wrapper">
      <van-empty description="缺少 quizId、objType 或 objId" />
    </div>

    <div v-else-if="loadError" class="state-wrapper">
      <van-empty description="加载失败，请稍后重试" />
    </div>

    <template v-else-if="quiz.id">
      <div class="quiz-main">
        <div class="qn-title-block">
          <i class="qn-title-accent" aria-hidden="true" />
          <h1 class="qn-title-text">{{ quizDisplayTitle }}</h1>
        </div>

        <div v-if="instructionsText" class="quiz-header">
          <div class="quiz-instructions">
            {{ instructionsText }}
          </div>
        </div>

        <div v-if="!isAnswerOpen" class="state-panel">
          当前考试已关闭作答
        </div>

        <div v-else class="quiz-content">
          <DynamicQuizPaper
            ref="quizPaper"
            :paper-config="paperConfig"
            :answer-config="answerConfig"
            :value="answerContent"
            :readonly="false"
            @state-change="onPaperStateChange"
          />
        </div>

        <div v-if="isAnswerOpen" class="footer-actions">
          <van-button
            block
            type="primary"
            round
            class="submit-btn"
            :disabled="!allQuestionsCorrect"
            :loading="submitting"
            @click="submitQuiz"
          >
            提交
          </van-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { Loading, Empty, Button, Toast } from 'vant'
import { setTitle } from '@/libs/ds-bridge'
import { getQuizDetail, getCurrentCompletionRecord, submitQuizCompletion } from '@/api/quiz'
import { resolveQuizReturnLocation } from '@/utils/quiz-return-url'
import DynamicQuizPaper from './components/DynamicQuizPaper'

Vue.use(Loading).use(Empty).use(Button).use(Toast)

export default {
  name: 'QuizPlay',
  components: { DynamicQuizPaper },
  data() {
    return {
      quizId: '',
      objType: '',
      objId: '',
      forcePlay: false,
      returnUrl: '',
      quiz: {},
      answerContent: {},
      allQuestionsCorrect: false,
      loading: false,
      submitting: false,
      loadError: false
    }
  },
  computed: {
    invalidQuery() {
      return !this.quizId || !this.objType || !this.objId
    },
    quizDisplayTitle() {
      return this.quiz.title || '考试详情'
    },
    paperConfig() {
      return this.quiz.paperConfig || {}
    },
    answerConfig() {
      return this.quiz.answerConfig || {}
    },
    instructionsText() {
      return this.paperConfig.instructions || ''
    },
    isAnswerOpen() {
      return this.quiz.isAnswerOpen === true
    }
  },
  watch: {
    '$route.query': {
      handler(query) {
        this.quizId = query.quizId || ''
        this.objType = query.objType || ''
        this.objId = query.objId || ''
        this.forcePlay = String(query.forcePlay || '') === '1'
        this.returnUrl = query.returnUrl || ''
        this.answerContent = {}
        this.allQuestionsCorrect = false
        if (!this.invalidQuery) {
          this.loadPage()
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.syncEmbeddedTitle()
  },
  methods: {
    syncEmbeddedTitle() {
      document.title = '调研问卷'
      setTitle('调研问卷')
    },
    routeQuery(extra) {
      var base = {
        quizId: this.quizId,
        objType: this.objType,
        objId: this.objId
      }
      if (this.returnUrl) {
        base.returnUrl = this.returnUrl
      }
      if (extra) {
        Object.keys(extra).forEach(function (k) { base[k] = extra[k] })
      }
      return base
    },
    unwrapResponse(response) {
      if (response && typeof response === 'object' && ('data' in response)) {
        return response.data
      }
      return response
    },
    loadPage() {
      this.loading = true
      this.loadError = false
      this.quiz = {}
      Promise.all([
        getQuizDetail(this.quizId, { objType: this.objType, objId: this.objId }),
        getCurrentCompletionRecord(this.quizId, { objType: this.objType, objId: this.objId })
      ])
        .then(function (results) {
          var quizRes = results[0]
          var recordRes = results[1]
          if (!quizRes || !recordRes) {
            this.loadError = true
            return
          }
          var quizData = this.unwrapResponse(quizRes)
          var recordData = this.unwrapResponse(recordRes)
          this.quiz = quizData || {}
          this.syncEmbeddedTitle()

          if (recordData && recordData.hasCompletionRecord && !this.forcePlay) {
            this.$router.replace({
              path: '/quiz/completion-record',
              query: this.routeQuery()
            }).catch(function () {})
          }
        }.bind(this))
        .catch(function () {
          this.loadError = true
        }.bind(this))
        .finally(function () {
          this.loading = false
        }.bind(this))
    },
    onPaperStateChange(payload) {
      this.answerContent = payload.answerContent
      this.allQuestionsCorrect = payload.allQuestionsCorrect
    },
    /**
     * 无合法 returnUrl 时：history 栈深度 > 1 则尝试 router.go(-1)，否则进入完成记录页（混合策略，best-effort）。
     */
    navigateAfterSubmitWithoutReturnUrl() {
      var self = this
      var canTryHistoryBack = typeof window !== 'undefined' &&
        window.history &&
        typeof window.history.length === 'number' &&
        window.history.length > 1
      if (canTryHistoryBack) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.info('[quiz][returnUrl] no returnUrl, try router.go(-1)', { historyLength: window.history.length })
        }
        self.$router.go(-1)
      } else {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.info('[quiz][returnUrl] no returnUrl, shallow history → completion-record', {
            historyLength: window.history.length
          })
        }
        self.$router.replace({
          path: '/quiz/completion-record',
          query: self.routeQuery()
        }).catch(function (err) {
          // eslint-disable-next-line no-console
          console.warn('[quiz][returnUrl] router.replace completion-record failed', err)
        })
      }
    },
    submitQuiz() {
      if (!this.isAnswerOpen) {
        Toast('当前考试已关闭作答')
        return
      }
      if (!this.allQuestionsCorrect) {
        Toast('请先完成全部题目的正确校验')
        return
      }
      this.submitting = true
      var paper = this.$refs.quizPaper
      var answerContent = (paper && paper.getAnswerContent) ? paper.getAnswerContent() : this.answerContent
      var self = this
      submitQuizCompletion(this.quizId, {
        objType: this.objType,
        objId: this.objId,
        answerContent: answerContent
      })
        .then(function (res) {
          if (!res) return
          Toast('提交成功')
          var returnLoc = resolveQuizReturnLocation(self.returnUrl)
          if (returnLoc) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.info('[quiz][returnUrl] submit success → router.replace', returnLoc)
            }
            self.$router.replace(returnLoc)
              .then(function () {
                if (process.env.NODE_ENV !== 'production') {
                  // eslint-disable-next-line no-console
                  console.info('[quiz][returnUrl] after replace', {
                    fullPath: self.$route.fullPath,
                    path: self.$route.path,
                    name: self.$route.name,
                    query: self.$route.query
                  })
                }
              })
              .catch(function (err) {
                // eslint-disable-next-line no-console
                console.warn('[quiz][returnUrl] router.replace failed', err)
              })
          } else {
            self.navigateAfterSubmitWithoutReturnUrl()
          }
        })
        .catch(function () {
          Toast('提交失败')
        })
        .finally(function () {
          self.submitting = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.quiz-play-page {
  min-height: 100vh;
  padding-bottom: calc(90px + constant(safe-area-inset-bottom));
  padding-bottom: calc(90px + env(safe-area-inset-bottom));
  background: #f5f6f7;
}

.quiz-main {
  padding: 12px 16px 0;
  box-sizing: border-box;
}

.loading-center {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.state-wrapper {
  padding-top: 40px;
}

.quiz-header {
  margin: 0 0 16px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
}

.quiz-instructions {
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.6;
  color: #646566;
  white-space: pre-wrap;
}

.state-panel {
  margin: 0 0 16px;
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff7e6;
  color: #8a4b00;
  font-size: 14px;
}

.quiz-content {
  padding: 0;
}

.footer-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 12px 16px;
  padding-bottom: calc(12px + constant(safe-area-inset-bottom));
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
}

.submit-btn {
  height: 44px;
  border: none;
  background: var(--qn-theme);
}
</style>
